import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AlertCircle, Check, CircleDashed, Loader2, Pencil, Plus, Sparkles, X,
} from 'lucide-react';
import {
  confirmGoal, createGoal, deleteGoal, fetchGoals, updateGoal,
  type GoalCard, type GoalHorizon, type GoalKind,
} from '../../orbit/lib/intakeApi';

/**
 * A company's goals — as many as they have, AI or not.
 *
 * Section D's five fields sit above this on the same card and are unchanged;
 * they capture the AI stance and one of them gates routing. What they cannot
 * hold is a second goal, or a goal like "open a second warehouse". These are
 * separate records, the same shape problems already use.
 *
 * A goal we inferred from the website arrives as `ai_guessed` and reads as a
 * suggestion with one tap to accept — the same distinction a guessed dossier
 * field gets, so the screen is consistent about what it knows versus what it
 * assumed.
 */

const HORIZONS: { value: GoalHorizon; label: string }[] = [
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'this_year',    label: 'This year' },
  { value: 'next_year',    label: 'Next year' },
  { value: 'ongoing',      label: 'Ongoing' },
];

const KINDS: { value: GoalKind; label: string }[] = [
  { value: 'business', label: 'Business goal' },
  { value: 'ai',       label: 'AI goal' },
];

const horizonLabel = (h: GoalHorizon | null) =>
  HORIZONS.find(x => x.value === h)?.label ?? null;

export interface GoalsPanelProps {
  clientId?: string;
  /** Told when the list changes, so a parent can refresh anything derived. */
  onChanged?: () => void;
}

export const GoalsPanel: React.FC<GoalsPanelProps> = ({ clientId, onChanged }) => {
  const [goals, setGoals] = useState<GoalCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setGoals(await fetchGoals(clientId));
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(null);
    }
  };

  const inferred = useMemo(() => goals.filter(g => g.state === 'ai_guessed'), [goals]);
  const settled = useMemo(() => goals.filter(g => g.state !== 'ai_guessed'), [goals]);

  return (
    <div>
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Your goals
      </span>

      {loading ? (
        <div className="h-16 animate-pulse rounded-xl bg-gray-100" />
      ) : goals.length === 0 ? (
        <p className="text-[12.5px] leading-relaxed text-gray-500">
          Nothing yet. A goal can be about AI or have nothing to do with it —
          both are worth us knowing.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {[...inferred, ...settled].map(goal => (
            <li key={goal.id}>
              {editingId === goal.id ? (
                <GoalForm
                  initial={goal}
                  busy={busy === goal.id}
                  onCancel={() => setEditingId(null)}
                  onSubmit={async (patch) => {
                    await act(goal.id, () => updateGoal(goal.id, { ...patch, clientId }));
                    setEditingId(null);
                  }}
                />
              ) : (
                <GoalRow
                  goal={goal}
                  busy={busy === goal.id}
                  onEdit={() => setEditingId(goal.id)}
                  onConfirm={() => act(goal.id, () => confirmGoal(goal.id, clientId))}
                  onDelete={() => act(goal.id, () => deleteGoal(goal.id, clientId))}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-2.5 inline-flex items-start gap-1.5 text-[11.5px] font-medium text-red-600">
          <AlertCircle size={12} strokeWidth={2.5} className="mt-px flex-none" />
          {error}
        </p>
      )}

      {/* Asked plainly, as the ask says — not hidden behind a plus icon alone. */}
      <div className="mt-3">
        {adding ? (
          <GoalForm
            busy={busy === '__new__'}
            onCancel={() => setAdding(false)}
            onSubmit={async (patch) => {
              await act('__new__', () => createGoal({
                statement: patch.statement ?? '',
                horizon: patch.horizon,
                kind: patch.kind,
                clientId,
              }));
              setAdding(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-brand-600 whitespace-nowrap"
          >
            <Plus size={13} strokeWidth={2.5} />
            {goals.length > 0 ? 'Is there another goal?' : 'Add a goal'}
          </button>
        )}
      </div>
    </div>
  );
};

const GoalRow: React.FC<{
  goal: GoalCard;
  busy: boolean;
  onEdit: () => void;
  onConfirm: () => void;
  onDelete: () => void;
}> = ({ goal, busy, onEdit, onConfirm, onDelete }) => {
  const guessed = goal.state === 'ai_guessed';
  return (
    <div
      className={clsx(
        'group flex items-start gap-2.5 rounded-xl px-3 py-2.5 ring-1',
        guessed ? 'bg-brand-50/40 ring-brand-100' : 'bg-white ring-gray-200',
      )}
    >
      <span className={clsx('mt-0.5 flex-none', guessed ? 'text-brand-500' : 'text-green-600')}>
        {guessed
          ? <CircleDashed size={13} strokeWidth={2.25} />
          : <Check size={13} strokeWidth={2.75} />}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-medium leading-snug text-gray-900">{goal.statement}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span
            className={clsx(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              goal.kind === 'ai'
                ? 'bg-brand-100 text-brand-700'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {goal.kind === 'ai' ? 'AI' : 'Business'}
          </span>
          {horizonLabel(goal.horizon) && (
            <span className="text-[11px] text-gray-400">{horizonLabel(goal.horizon)}</span>
          )}
          {guessed && (
            <span className="inline-flex items-center gap-1 text-[11px] text-brand-600">
              <Sparkles size={10} strokeWidth={2.5} />
              We inferred this
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-none items-center gap-0.5">
        {guessed && (
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-md px-2 py-1 text-[11px] font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : 'Keep'}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onEdit}
          className="rounded-md p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-brand-500 group-hover:opacity-100 disabled:opacity-50"
          aria-label={`Edit goal: ${goal.statement}`}
        >
          <Pencil size={12} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          className="rounded-md p-1.5 text-gray-300 opacity-0 transition-opacity hover:bg-gray-100 hover:text-red-600 group-hover:opacity-100 disabled:opacity-50"
          aria-label={`Remove goal: ${goal.statement}`}
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

const GoalForm: React.FC<{
  initial?: GoalCard;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (patch: { statement?: string; horizon?: GoalHorizon | null; kind?: GoalKind }) => void;
}> = ({ initial, busy, onCancel, onSubmit }) => {
  const [statement, setStatement] = useState(initial?.statement ?? '');
  const [kind, setKind] = useState<GoalKind>(initial?.kind ?? 'business');
  const [horizon, setHorizon] = useState<GoalHorizon | null>(initial?.horizon ?? 'this_year');

  const ready = statement.trim().length >= 3;

  return (
    <div className="rounded-xl bg-brand-50/50 p-3.5 ring-1 ring-brand-100">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-gray-900">
          {initial ? 'Edit this goal' : 'What are you trying to achieve?'}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          aria-label="Cancel"
        >
          <X size={13} strokeWidth={2.25} />
        </button>
      </div>

      <input
        value={statement}
        onChange={e => setStatement(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && ready) onSubmit({ statement: statement.trim(), kind, horizon });
        }}
        autoFocus
        placeholder="Open a second warehouse"
        aria-label="Goal"
        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />

      <div className="mt-3 flex flex-wrap gap-1.5">
        {KINDS.map(k => (
          <button
            key={k.value}
            type="button"
            onClick={() => setKind(k.value)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              kind === k.value
                ? 'border-brand-500 bg-brand-500 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-brand-500',
            )}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {HORIZONS.map(h => (
          <button
            key={h.value}
            type="button"
            onClick={() => setHorizon(horizon === h.value ? null : h.value)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              horizon === h.value
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
            )}
          >
            {h.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        disabled={!ready || busy}
        onClick={() => onSubmit({ statement: statement.trim(), kind, horizon })}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
      >
        {busy && <Loader2 size={12} strokeWidth={2.5} className="animate-spin" />}
        {initial ? 'Save' : 'Add goal'}
      </button>
    </div>
  );
};

function messageFor(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't update your goals.";
}

export default GoalsPanel;
